import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import {
  FiSearch, FiFileText, FiClock, FiMessageSquare, FiRefreshCw,
  FiThumbsUp, FiThumbsDown, FiCheckCircle, FiAlertCircle,
} from 'react-icons/fi';
import '../../styles/QuoteProposal.css';
import '../../styles/Voice.css';
import '../commissionscan/CommissionScan.css';
import '../../styles/AcordSmartScanHistory.css';
import PreviewIcon from '../../assets/images/ViewDetailsIcon.svg';
import {
  getToolboxToken, getNcToken,
  isDecOrQuote, findBillingTypeByValue, BILLING_TYPE_DATA,
  type NowCertsClient,
} from './acordSmartScanShared';
import { SortablePaginatedTable, CoverageLimitCard, InsuredDetailsReadOnlyGrid, getOptionStyle } from './AcordSmartScanReviewParts';
import { submitAcordSmartScanToNowCerts } from './acordSmartScanSubmit';
import { useTheme } from '../../context/ThemeContext';
import { useToast } from '../../context/ToastContext';

const PAGE_SIZE = 10;

const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  Success: { label: 'Success', color: '#34d399' },
  Failed: { label: 'Failed', color: '#f87171' },
};

const formatDate = (d: string): string => {
  if (!d) return '—';
  try {
    const dt = new Date(d);
    const m = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${m[dt.getMonth()]} ${dt.getDate()}, ${dt.getFullYear()}`;
  } catch { return d; }
};

const DOC_TYPE_LABELS: Record<string, string> = {
  Acord125: 'Acord 125', Acord126: 'Acord 126', Acord130: 'Acord 130',
  DecPage: 'Dec Page', QuoteLetter: 'Quote Letter',
};

interface ScanTransaction {
  transactionId?: string;
  submissionId?: string;
  fileName: string;
  created: string;
  userName: string;
  status: string;
  documentType?: string;
  responseJson?: string;
  requestJson?: string;
  selectedOptions?: string;
  isLiked?: boolean | null;
  isUpdateInNowCerts?: boolean;
  totalCount?: number;
}

const getTxId = (tx: ScanTransaction) => tx.transactionId || tx.submissionId || '';

const getSelectedOptions = (tx: ScanTransaction): any => {
  try { return tx.selectedOptions ? JSON.parse(tx.selectedOptions) : {}; } catch { return {}; }
};

const getClientName = (tx: ScanTransaction): string => getSelectedOptions(tx).ClientName || '—';

/* ════════════════════════════════ */
const AcordSmartScanHistory: React.FC = () => {
  const { theme } = useTheme();
  const optionStyle = getOptionStyle(theme);
  const { showSuccess, showError } = useToast();
  const [rows, setRows] = useState<ScanTransaction[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  /* Preview */
  const [previewTx, setPreviewTx] = useState<ScanTransaction | null>(null);
  const [previewPdfUrl, setPreviewPdfUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState<any>(null);
  const [previewTab, setPreviewTab] = useState<'Insured' | 'Coverage' | 'Property' | 'Vehicle' | 'Driver' | 'Fees'>('Insured');
  const [previewSelectedOptions, setPreviewSelectedOptions] = useState<any>(null);
  const [previewFieldErrors, setPreviewFieldErrors] = useState<Record<string, string>>({});
  const [previewSubmitLoading, setPreviewSubmitLoading] = useState(false);
  const [previewSubmitResults, setPreviewSubmitResults] = useState<Array<{ label: string; success: boolean; message: string }>>([]);
  const [previewShowSubmitConfirm, setPreviewShowSubmitConfirm] = useState(false);
  const [previewNcUrl, setPreviewNcUrl] = useState('');
  const [previewCreatedNewClient, setPreviewCreatedNewClient] = useState(false);

  /* Upload to AMS */
  const [uploadAmsOpen, setUploadAmsOpen] = useState(false);
  const [uploadAmsClientQuery, setUploadAmsClientQuery] = useState('');
  const [uploadAmsClientList, setUploadAmsClientList] = useState<NowCertsClient[]>([]);
  const [uploadAmsClientLoading, setUploadAmsClientLoading] = useState(false);
  const [uploadAmsSelectedClient, setUploadAmsSelectedClient] = useState<NowCertsClient | null>(null);
  const [uploadAmsLoading, setUploadAmsLoading] = useState(false);
  const [uploadAmsError, setUploadAmsError] = useState('');
  const [uploadAmsSuccess, setUploadAmsSuccess] = useState(false);

  /* Feedback */
  const [feedbackTxId, setFeedbackTxId] = useState<string | null>(null);
  const [feedbackText, setFeedbackText] = useState('');
  const [feedbackRating, setFeedbackRating] = useState<boolean | null>(null);
  const [feedbackAnchor, setFeedbackAnchor] = useState<{ top: number; right: number } | null>(null);
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [feedBackError, setFeedBackError] = useState('');
  const feedbackRef = useRef<HTMLDivElement>(null);

  /* ── Fetch history ── */
  const fetchData = useCallback(async (pageNum: number, signal?: AbortSignal) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_DataFabric_Url}/api/OCR/getsmartscanhistory?pageNumber=${pageNum}&recordCount=${PAGE_SIZE}`,
        { headers: { Authorization: `Bearer ${getToolboxToken()}`, accept: 'application/json' }, signal },
      );
      if (!res.ok) throw new Error(`Failed to load history (${res.status})`);
      const data: ScanTransaction[] = await res.json();
      setRows(Array.isArray(data) ? data : []);
      setTotalCount(Array.isArray(data) && data.length > 0 ? (data[0].totalCount ?? data.length) : 0);
    } catch (e: any) {
      if (e.name === 'AbortError') return;
      const message = e.message || 'Unknown error while getting SmartScan history';
      setError(message);
      showError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let controller: AbortController | undefined;
    const timer = setTimeout(() => {
      controller = new AbortController();
      fetchData(page, controller.signal);
    }, 0);
    return () => { clearTimeout(timer); controller?.abort(); };
  }, [fetchData, page]);

  const filtered = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.toLowerCase();
    return rows.filter((r) =>
      r.fileName?.toLowerCase().includes(q) ||
      r.userName?.toLowerCase().includes(q) ||
      getClientName(r).toLowerCase().includes(q) ||
      (r.documentType || '').toLowerCase().includes(q) ||
      r.status?.toLowerCase().includes(q),
    );
  }, [rows, search]);

  useEffect(() => { setPage(1); }, [search]);

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const pageList = Array.from({ length: totalPages }, (_, i) => i + 1)
    .filter((p) => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
    .reduce<(number | '…')[]>((acc, p, idx, arr) => {
      if (idx > 0 && (p as number) - (arr[idx - 1] as number) > 1) acc.push('…');
      acc.push(p);
      return acc;
    }, []);

  /* ── Preview ── */
  const onPreviewClick = useCallback(async (tx: ScanTransaction) => {
    setPreviewTx(tx);
    setPreviewPdfUrl(null);
    setPreviewData(null);
    setPreviewTab('Insured');
    setPreviewLoading(true);
    setPreviewFieldErrors({});
    setPreviewSubmitResults([]);
    setPreviewShowSubmitConfirm(false);
    setPreviewNcUrl('');
    setPreviewCreatedNewClient(false);
    setPreviewSelectedOptions(getSelectedOptions(tx));

    try {
      /* Insured fields follow isUpdateInNowCerts: the raw scan result (responseJson)
       * before it's synced, the actually-submitted/edited data (requestJson) after —
       * but property/vehicle/driver/fees/coverage always come from the raw scan
       * result, since those aren't user-editable. Mirrors onPreviewClick() in the
       * legacy SmartScanHistory.tsx. */
      const isUpdated = !!tx.isUpdateInNowCerts;
      const responseJson = tx.responseJson ? JSON.parse(tx.responseJson) : null;
      const requestJson = tx.requestJson ? JSON.parse(tx.requestJson) : null;
      const insuredSource = isUpdated ? (requestJson || responseJson) : responseJson;

      if (insuredSource || responseJson) {
        const parsed = insuredSource || {};
        setPreviewData({
          insured: {
            businessName: parsed.businessName || '', firstName: parsed.firstName || '', lastName: parsed.lastName || '',
            email: parsed.email || '', phoneNumber: parsed.phoneNumber || '', policyNumber: parsed.policyNumber || '',
            policyPremium: parsed.policyPremium || '', carrierName: parsed.carrierName || '',
            lineOfBusiness: Array.isArray(parsed.linesOfBusiness) ? parsed.linesOfBusiness[0] : (parsed.linesOfBusiness || parsed.lineOfBusiness || ''),
            quoteNumber: parsed.quoteNumber || '', insuredType: parsed.insuredType || '',
            effectiveDate: parsed.effectiveDate || '', expirationDate: parsed.expirationDate || '',
            address: parsed.address || '', city: parsed.city || '', state: parsed.state || '', zipCode: parsed.zipCode || '',
          },
          coverageLimitDetails: responseJson?.coverageLimitDetails || null,
          propertyDetails: Array.isArray(responseJson?.propertyDetails) ? responseJson.propertyDetails : (responseJson?.propertyDetails ? [responseJson.propertyDetails] : []),
          vehicleDetails: Array.isArray(responseJson?.vehicleDetails) ? responseJson.vehicleDetails : [],
          driverDetails: Array.isArray(responseJson?.driverDetails) ? responseJson.driverDetails : [],
          taxesAndFees: Array.isArray(responseJson?.taxesAndFees) ? responseJson.taxesAndFees : [],
        });
      }
    } catch { /* silent */ }

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_DataFabric_Url}/api/OCR/previewdocument`,
        { TransactionId: getTxId(tx), FileName: tx.fileName, Target: 'SmartScan' },
        { headers: { Authorization: `Bearer ${getToolboxToken()}` } },
      );
      setPreviewPdfUrl(res.data?.fileUrl ?? null);
    } catch { /* silent */ }
    finally { setPreviewLoading(false); }
  }, []);

  const closePreview = useCallback(() => {
    setPreviewTx(null);
    setPreviewPdfUrl(null);
    setPreviewData(null);
  }, []);

  const updatePreviewInsured = useCallback((patch: Record<string, any>) => {
    setPreviewData((prev: any) => (prev ? { ...prev, insured: { ...prev.insured, ...patch } } : prev));
  }, []);

  const updatePreviewFee = useCallback((index: number, patch: Record<string, any>) => {
    setPreviewData((prev: any) => prev ? {
      ...prev,
      taxesAndFees: prev.taxesAndFees.map((f: any, i: number) => (i === index ? { ...f, ...patch } : f)),
    } : prev);
  }, []);

  /* ── Submit from preview — same NowCerts push chain as the live-scan review,
   * reconstructed from the row's stored selectedOptions. Only relevant when the
   * record hasn't been synced yet (isUpdateInNowCerts === false). ── */
  const onPreviewSubmitClick = useCallback(() => {
    if (!previewData) return;
    const errors: Record<string, string> = {};
    if (previewData.insured.insuredType === 'Personal') {
      if (!previewData.insured.firstName?.trim() || !previewData.insured.lastName?.trim()) errors.firstName = 'First and last name are required';
    } else if (!previewData.insured.businessName?.trim()) {
      errors.businessName = 'Business name is required';
    }
    const opts = previewSelectedOptions || {};
    if (opts.CreateNewPolicy && !previewData.insured.policyNumber?.trim()) errors.policyNumber = 'Policy number is required';
    if (opts.CreateQuote && !previewData.insured.quoteNumber?.trim()) errors.quoteNumber = 'Quote number is required';
    setPreviewFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;
    setPreviewShowSubmitConfirm(true);
  }, [previewData, previewSelectedOptions]);

  const onPreviewSubmitConfirmed = useCallback(async () => {
    if (!previewTx || !previewData) return;
    setPreviewShowSubmitConfirm(false);
    setPreviewSubmitLoading(true);
    setPreviewSubmitResults([]);
    try {
      const opts = previewSelectedOptions || {};
      const isProspect = !!opts.SelectProspect;

      const { ncUrl, createdNewClient } = await submitAcordSmartScanToNowCerts({
        insured: previewData.insured,
        coverageLimitDetails: previewData.coverageLimitDetails,
        propertyDetails: previewData.propertyDetails,
        vehicleDetails: previewData.vehicleDetails,
        driverDetails: previewData.driverDetails,
        taxesAndFees: previewData.taxesAndFees,
        documentType: previewTx.documentType || '',
        submissionId: getTxId(previewTx),
        isProspect,
        createNewClient: !!opts.CreateNewClient,
        createQuote: !!opts.CreateQuote,
        insuredId: opts.InsuredId || '',
      }, (step) => setPreviewSubmitResults((prev) => [...prev, step]));

      setPreviewNcUrl(ncUrl);
      setPreviewCreatedNewClient(createdNewClient);
      setRows((prev) => prev.map((r) => (getTxId(r) === getTxId(previewTx) ? { ...r, isUpdateInNowCerts: true } : r)));
      setPreviewTx((prev) => (prev ? { ...prev, isUpdateInNowCerts: true } : prev));
    } finally {
      setPreviewSubmitLoading(false);
    }
  }, [previewTx, previewData, previewSelectedOptions]);

  /* ── Upload to AMS ── */
  useEffect(() => {
    const q = uploadAmsClientQuery;
    if (!q.trim()) { setUploadAmsClientList([]); return; }
    setUploadAmsClientLoading(true);
    const t = setTimeout(() => {
      axios.get(`${process.env.REACT_APP_NOWCERTSAPI}/api/nowcert/customerlist`, {
        params: { searchText: q, nowCertsToken: getNcToken() },
        headers: { Authorization: `Bearer ${getToolboxToken()}` },
      })
        .then((r) => setUploadAmsClientList(r.data || []))
        .finally(() => setUploadAmsClientLoading(false));
    }, 400);
    return () => clearTimeout(t);
  }, [uploadAmsClientQuery]);

  const openUploadAms = useCallback(() => {
    setUploadAmsClientQuery('');
    setUploadAmsClientList([]);
    setUploadAmsSelectedClient(null);
    setUploadAmsError('');
    setUploadAmsSuccess(false);
    setUploadAmsOpen(true);
  }, []);

  const onUploadToAMSConfirmed = useCallback(async () => {
    if (!uploadAmsSelectedClient || !previewTx) return;
    setUploadAmsLoading(true);
    setUploadAmsError('');
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_NOWCERTSAPI}/api/nowcert/uploadV1`,
        {
          TransactionId: getTxId(previewTx),
          FileName: previewTx.fileName,
          NCPolicyId: '',
          NCInsuredId: uploadAmsSelectedClient.databaseId,
          FileData: '',
          FolderName: 'AcordAndDecPageScan',
          TargetName: 'AcordAndDecPageScan',
          NowCertsToken: getNcToken(),
        },
        { headers: { Authorization: `Bearer ${getToolboxToken()}` } },
      );
      if (res.data.success) {
        setUploadAmsSuccess(true);
        setRows((prev) => prev.map((r) => (getTxId(r) === getTxId(previewTx) ? { ...r, isUpdateInNowCerts: true } : r)));
        setTimeout(() => { setUploadAmsOpen(false); setUploadAmsSuccess(false); }, 1500);
      } else {
        setUploadAmsError(res.data.errorMessage || 'Error while uploading PDF to AMS.');
      }
    } catch { setUploadAmsError('Error while uploading PDF to AMS.'); }
    finally { setUploadAmsLoading(false); }
  }, [previewTx, uploadAmsSelectedClient]);

  /* ── Feedback ── */
  const openFeedback = useCallback((tx: ScanTransaction, e: React.MouseEvent<HTMLButtonElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setFeedbackTxId(getTxId(tx));
    setFeedbackText('');
    setFeedbackRating(tx.isLiked ?? null);
    setFeedBackError('');
    setFeedbackAnchor({ top: rect.bottom + 8, right: window.innerWidth - rect.right });
  }, []);

  const closeFeedback = useCallback(() => {
    setFeedbackTxId(null);
    setFeedbackAnchor(null);
    setFeedbackText('');
    setFeedbackRating(null);
    setFeedBackError('');
  }, []);

  const sendFeedback = (isLiked: boolean | null, text: string) => {
    if (!feedbackTxId) return;
    axios.post(
      `${process.env.REACT_APP_DataFabric_Url}/api/NCAICommissionStatement/feedback`,
      { TransactionId: feedbackTxId, IsLiked: isLiked, Feedback: text, Target: 3 },
      { headers: { Authorization: `Bearer ${getToolboxToken()}` } },
    )
      .then(() => showSuccess('Feedback submitted successfully!'))
      .catch(() => {});
  };

  const onThumbsUpClick = useCallback(() => {
    if (!feedbackTxId) return;
    const cur = rows.find((r) => getTxId(r) === feedbackTxId);
    const next: boolean | null = cur?.isLiked === true ? null : true;
    setRows((prev) => prev.map((r) => (getTxId(r) === feedbackTxId ? { ...r, isLiked: next } : r)));
    closeFeedback();
    sendFeedback(next, feedbackText ?? '');
  }, [feedbackTxId, rows, feedbackText, closeFeedback]);

  const onThumbsDownClick = useCallback(() => {
    if (!feedbackTxId) return;
    const cur = rows.find((r) => getTxId(r) === feedbackTxId);
    const next: boolean | null = cur?.isLiked === false ? null : false;
    setRows((prev) => prev.map((r) => (getTxId(r) === feedbackTxId ? { ...r, isLiked: next } : r)));
    closeFeedback();
    sendFeedback(next, feedbackText ?? '');
  }, [feedbackTxId, rows, feedbackText, closeFeedback]);

  const onSubmitFeedbackClick = useCallback(() => {
    if (!feedbackTxId) return;
    if (!feedbackText.trim()) { setFeedBackError('Please enter your feedback'); return; }
    if (feedbackText.length > 250) { setFeedBackError('Please provide your feedback within 250 character'); return; }
    setFeedBackError('');
    setFeedbackSubmitting(true);
    axios.post(
      `${process.env.REACT_APP_DataFabric_Url}/api/NCAICommissionStatement/feedback`,
      { TransactionId: feedbackTxId, IsLiked: feedbackRating, Feedback: feedbackText, Target: 3 },
      { headers: { Authorization: `Bearer ${getToolboxToken()}` } },
    )
      .then(() => { setFeedbackSubmitting(false); showSuccess('Feedback submitted successfully'); closeFeedback(); })
      .catch((err) => {
        setFeedbackSubmitting(false);
        setFeedBackError(err?.response?.data?.errorMessage || 'Error submitting feedback. Please try again.');
      });
  }, [feedbackTxId, feedbackText, feedbackRating, closeFeedback]);

  useEffect(() => {
    if (!feedbackTxId) return;
    const handler = (e: MouseEvent) => {
      if (feedbackRef.current && !feedbackRef.current.contains(e.target as Node)) closeFeedback();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [feedbackTxId, closeFeedback]);

  const PREVIEW_TABS: Array<{ key: typeof previewTab; label: string; show: boolean }> = previewData ? [
    { key: 'Insured', label: 'Insured', show: true },
    { key: 'Coverage', label: 'Coverage', show: isDecOrQuote(previewTx?.documentType || '') },
    { key: 'Property', label: 'Property', show: isDecOrQuote(previewTx?.documentType || '') },
    { key: 'Vehicle', label: 'Vehicle', show: isDecOrQuote(previewTx?.documentType || '') },
    { key: 'Driver', label: 'Driver', show: isDecOrQuote(previewTx?.documentType || '') },
    { key: 'Fees', label: 'Fees', show: (previewData.taxesAndFees || []).length > 0 },
  ] : [];

  /* ════════════════════ RENDER ════════════════════ */
  return (
    <>
      <motion.div className="qp-history" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}>
        <div className="qp-history__header">
          <h3 className="qp-history__title acord-smartscan-history__title-reset">Acord and Dec Pages Scan History</h3>
          <div className="acord-smartscan-history__header-actions">
            <div className="qp-history__search">
              <FiSearch size={13} />
              <input type="text" placeholder="Search…" value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            <motion.button className="csh-refresh-btn" onClick={() => fetchData(page)} whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
              <FiRefreshCw size={13} className={loading && rows.length > 0 ? 'csh-spin' : ''} />
              Refresh
            </motion.button>
          </div>
        </div>

        <div className="qp-history__body">
          {error ? (
            <div className="qp-empty-state acord-smartscan-history__error-state">
              <FiFileText size={28} /><p>{error}</p>
            </div>
          ) : loading ? (
            <div className="qp-history-loader acord-smartscan-history__loader-height">
              <div className="qp-history-loader__orb">
                <div className="qp-history-loader__ring qp-history-loader__ring--1" />
                <div className="qp-history-loader__ring qp-history-loader__ring--2" />
                <div className="qp-history-loader__core"><FiClock size={16} /></div>
              </div>
              <div className="qp-history-loader__text">
                <p className="qp-history-loader__title">Loading History</p>
                <p className="qp-history-loader__sub">
                  Fetching scan records<span className="qp-init-loader__dots"><span>.</span><span>.</span><span>.</span></span>
                </p>
              </div>
            </div>
          ) : filtered.length === 0 ? (
            <div className="qp-empty-state">
              <FiFileText size={28} />
              <p>{search ? 'No records match your search' : 'No Smart Scans yet.'}</p>
            </div>
          ) : (
            <div className="qp-table-wrap glass-surface">
              <table className="qp-table">
                <thead>
                  <tr>
                    <th className="qp-table__th acord-smartscan-history__th-center">File Name</th>
                    <th className="qp-table__th acord-smartscan-history__th-center">Submitted Date</th>
                    <th className="qp-table__th acord-smartscan-history__th-center">User</th>
                    <th className="qp-table__th acord-smartscan-history__th-center">Document Type</th>
                    <th className="qp-table__th acord-smartscan-history__th-center">Updated In AMS</th>
                    <th className="qp-table__th acord-smartscan-history__th-status">Status</th>
                    <th className="qp-table__th acord-smartscan-history__th-center">Action</th>
                    <th className="qp-table__th acord-smartscan-history__th-center">Feedback</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((tx, i) => {
                    const isSuccess = tx.status === 'Success';
                    return (
                      <tr key={getTxId(tx) || i} className="qp-table__row">
                        <td className="qp-table__td acord-smartscan-history__filename-cell">
                          {tx.fileName}
                        </td>
                        <td className="qp-table__td acord-smartscan-history__td-center-nowrap">{formatDate(tx.created)}</td>
                        <td className="qp-table__td acord-smartscan-history__td-center">{tx.userName}</td>
                        <td className="qp-table__td acord-smartscan-history__td-center">{DOC_TYPE_LABELS[tx.documentType || ''] || tx.documentType || '—'}</td>
                        <td className="qp-table__td acord-smartscan-history__td-center-nowrap">
                          <span className="acord-smartscan-history__status-badge" style={{
                            background: tx.isUpdateInNowCerts ? 'rgba(16,185,129,0.12)' : 'rgba(239,68,68,0.1)',
                            color: tx.isUpdateInNowCerts ? '#10b981' : '#ef4444',
                          }}>
                            {tx.isUpdateInNowCerts ? 'Yes' : 'No'}
                          </span>
                        </td>
                        <td className="qp-table__td acord-smartscan-history__td-center-nowrap">
                          <span className="qp-status-pill" style={{ color: (STATUS_CONFIG[tx.status] || STATUS_CONFIG.Failed).color }}>
                            {(STATUS_CONFIG[tx.status] || { label: tx.status }).label}
                          </span>
                        </td>
                        <td className="qp-table__td acord-smartscan-history__td-center">
                          <div className="acord-smartscan-history__flex-center">
                            {isSuccess ? (
                              <button className="qp-table__action-btn" title="Preview" onClick={() => onPreviewClick(tx)}>
                                <img src={PreviewIcon} alt="Preview" width={30} height={30} />
                              </button>
                            ) : (
                              <span className="acord-smartscan-history__not-available-text">Not Available</span>
                            )}
                          </div>
                        </td>
                        <td className="qp-table__td acord-smartscan-history__td-center">
                          <div className="acord-smartscan-history__flex-center">
                            <button className="qp-table__action-btn" title="Feedback" onClick={(e) => openFeedback(tx, e)}>
                              <FiMessageSquare size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {totalCount > 0 && (
            <div className="qp-pagination">
              <span className="qp-pagination__info">
                {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, totalCount)} of {totalCount}
              </span>
              <div className="qp-pagination__controls">
                <button className="qp-pagination__btn" disabled={page === 1} onClick={() => setPage(1)}>«</button>
                <button className="qp-pagination__btn" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>‹</button>
                {pageList.map((p, i) =>
                  p === '…'
                    ? <span key={`e-${i}`} className="qp-pagination__ellipsis">…</span>
                    : <button key={p} className={`qp-pagination__btn${page === p ? ' qp-pagination__btn--active' : ''}`} onClick={() => setPage(p as number)}>{p}</button>,
                )}
                <button className="qp-pagination__btn" disabled={page === totalPages} onClick={() => setPage((p) => p + 1)}>›</button>
                <button className="qp-pagination__btn" disabled={page === totalPages} onClick={() => setPage(totalPages)}>»</button>
              </div>
            </div>
          )}
        </div>
      </motion.div>

      {/* ── Feedback Popup ── */}
      {feedbackTxId && feedbackAnchor && (
        <div ref={feedbackRef} className="csfb-popup" style={{ top: feedbackAnchor.top, right: feedbackAnchor.right }}>
          <h4 className="csfb-title">Feedback</h4>
          <textarea className="csfb-textarea" placeholder="Enter your feedback here" value={feedbackText} onChange={(e) => setFeedbackText(e.target.value)} rows={4} />
          <div className="csfb-rating">
            <button className={`csfb-rating-btn${feedbackRating === true ? ' csfb-rating-btn--up' : ''}`} onClick={onThumbsUpClick} title="Thumbs up"><FiThumbsUp size={20} /></button>
            <button className={`csfb-rating-btn${feedbackRating === false ? ' csfb-rating-btn--down' : ''}`} onClick={onThumbsDownClick} title="Thumbs down"><FiThumbsDown size={20} /></button>
          </div>
          {feedBackError && <p className="csfb-error">{feedBackError}</p>}
          <div className="csfb-actions">
            <button className="csfb-btn-cancel" onClick={closeFeedback}>Cancel</button>
            <button className="csfb-btn-submit" disabled={feedbackSubmitting} onClick={onSubmitFeedbackClick}>
              {feedbackSubmitting ? 'Submitting…' : 'Submit'}
            </button>
          </div>
        </div>
      )}

      {/* ── Upload to AMS Dialog ── */}
      {uploadAmsOpen && ReactDOM.createPortal(
        <div className="csams-overlay" onClick={() => setUploadAmsOpen(false)}>
          <div className="csams-modal" onClick={(e) => e.stopPropagation()}>
            <div className="csams-modal__header">
              <h3 className="csams-modal__title">Upload In AMS</h3>
              <button className="csams-modal__close" onClick={() => setUploadAmsOpen(false)}>✕</button>
            </div>
            <div className="csams-modal__body">
              <label className="csams-label">Search Client</label>
              <div className="csams-search-wrap">
                <input
                  className="csams-search-input"
                  placeholder="Enter client name..."
                  value={uploadAmsClientQuery}
                  onChange={(e) => { setUploadAmsClientQuery(e.target.value); setUploadAmsSelectedClient(null); }}
                  autoFocus
                />
                {uploadAmsClientLoading && <span className="csams-search-spinner" />}
                {uploadAmsClientList.length > 0 && !uploadAmsSelectedClient && (
                  <ul className="csams-dropdown">
                    {uploadAmsClientList.map((c) => (
                      <li key={c.databaseId} className="csams-dropdown__item" onClick={() => { setUploadAmsSelectedClient(c); setUploadAmsClientQuery(c.name); setUploadAmsClientList([]); }}>
                        {c.name}
                        {c.eMail && <span className="csams-dropdown__email">{c.eMail}</span>}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              {uploadAmsSuccess && <p className="csams-success">PDF uploaded to AMS successfully</p>}
              {uploadAmsError && <p className="csams-error">{uploadAmsError}</p>}
            </div>
            <div className="csams-modal__footer">
              <button className="csams-btn csams-btn--cancel" onClick={() => setUploadAmsOpen(false)}>Cancel</button>
              <button className="csams-btn csams-btn--upload" disabled={!uploadAmsSelectedClient || uploadAmsLoading} onClick={onUploadToAMSConfirmed}>
                {uploadAmsLoading ? 'Uploading…' : 'Upload'}
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}

      {/* ── Preview Modal ── */}
      {previewTx && ReactDOM.createPortal(
        <div className="csams-overlay acord-smartscan-history__preview-overlay-zindex" onClick={closePreview}>
          <div
            onClick={(e) => e.stopPropagation()}
            className="acord-smartscan-history__preview-modal"
          >
            <div className="acord-smartscan-history__preview-header">
              <div className="acord-smartscan-history__preview-header-info">
                {(() => {
                  const opts = previewSelectedOptions || {};
                  const summary: Array<{ label: string; value: string }> = [];
                  if (opts.SelectProspect) {
                    summary.push({ label: 'Prospect', value: opts.UseExistingProspect ? (opts.ClientName || '—') : 'Create Prospect' });
                    summary.push({ label: 'Quote', value: opts.CreateQuote ? 'Create Quote' : '—' });
                  } else if (opts.SelectInsured) {
                    summary.push({ label: 'Insured', value: opts.UseExistingInsured ? (opts.ClientName || '—') : 'Create Insured' });
                    summary.push({ label: 'Quote', value: opts.CreateQuote ? 'Create Quote' : '—' });
                    summary.push({ label: 'Policy', value: opts.CreateNewPolicy ? 'Create Policy' : '—' });
                  } else {
                    summary.push({ label: 'Client', value: getClientName(previewTx) });
                  }
                  return summary.map((it, i) => (
                    <span key={i} className="acord-smartscan-history__summary-item">
                      <strong className="acord-smartscan-history__summary-label">{it.label}:</strong>{' '}
                      <span className="acord-smartscan-history__summary-value">{it.value}</span>
                    </span>
                  ));
                })()}
                {previewTx.isUpdateInNowCerts && (
                  <span className="acord-smartscan-history__updated-badge">
                    ✓ Updated in AMS
                  </span>
                )}
              </div>
              <button onClick={closePreview} className="acord-smartscan-history__close-btn">✕</button>
            </div>

            <div className="acord-smartscan-history__preview-body">
              <div className="cspr-pdf-panel">
                <div className="cspr-pdf-panel__header">
                  <span className="cspr-pdf-panel__title">Scanned Document</span>
                  <motion.button className="cspr-action-btn cspr-action-btn--purple acord-smartscan-history__upload-btn-padding" onClick={openUploadAms} whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                    Upload In AMS
                  </motion.button>
                </div>
                <div className="cspr-pdf-viewer">
                  {previewPdfUrl
                    ? <iframe src={`${previewPdfUrl}#zoom=90`} title="Document Preview" className="acord-smartscan-history__pdf-iframe" />
                    : previewLoading
                      ? <div className="cspr-pdf-placeholder">Loading document…</div>
                      : <div className="cspr-pdf-placeholder">Preview not available</div>}
                </div>
              </div>

              <div className="acord-smartscan-history__preview-details">
                {!previewData ? (
                  <p className="acord-smartscan-history__no-data-text">No extracted data available.</p>
                ) : (
                  <>
                    <div className="cs-tabs acord-smartscan-history__tabs-wrap">
                      {PREVIEW_TABS.filter((t) => t.show).map((t) => (
                        <button key={t.key} className={`cs-tab${previewTab === t.key ? ' cs-tab--active' : ''}`} onClick={() => setPreviewTab(t.key)}>
                          {t.label}
                        </button>
                      ))}
                    </div>

                    {previewTab === 'Insured' && (
                      <InsuredDetailsReadOnlyGrid
                        insured={previewData.insured}
                        isReadOnly={!!previewTx.isUpdateInNowCerts}
                        onChange={updatePreviewInsured}
                        fieldErrors={previewFieldErrors}
                      />
                    )}

                    {previewTab === 'Coverage' && (
                      <CoverageLimitCard lineOfBusiness={previewData.insured.lineOfBusiness} coverageLimitDetails={previewData.coverageLimitDetails} />
                    )}

                    {previewTab === 'Property' && (
                      <SortablePaginatedTable
                        title="Property Details"
                        rows={previewData.propertyDetails.map((p: any) => ({
                          'Address Line 1': p.AddressLine1 || '', City: p.City || '', State: p.State || '',
                          'Zip Code': p.ZipCode || '', 'Property Use': p.PropertyUseCode || '',
                        }))}
                        columns={[
                          { key: 'Address Line 1', label: 'Address Line 1' },
                          { key: 'City', label: 'City' },
                          { key: 'State', label: 'State' },
                          { key: 'Zip Code', label: 'Zip Code' },
                          { key: 'Property Use', label: 'Property Use' },
                        ]}
                        emptyLabel="No property details found."
                      />
                    )}

                    {previewTab === 'Vehicle' && (
                      <SortablePaginatedTable
                        title="Vehicle Details"
                        rows={previewData.vehicleDetails.map((v: any) => ({ Make: v.Make || '', Model: v.Model || '', Year: v.Year || '', VIN: v.Vin || '' }))}
                        columns={[
                          { key: 'Make', label: 'Make' },
                          { key: 'Model', label: 'Model' },
                          { key: 'Year', label: 'Year' },
                          { key: 'VIN', label: 'VIN' },
                        ]}
                        emptyLabel="No vehicle details found."
                      />
                    )}

                    {previewTab === 'Driver' && (
                      <SortablePaginatedTable
                        title="Driver Details"
                        rows={previewData.driverDetails.map((d: any) => ({
                          'First Name': d.FirstName || '', 'Last Name': d.LastName || '',
                          'Date Of Birth': d.DateOfBirth || '', 'License Number': d.LicenseNumber || '',
                        }))}
                        columns={[
                          { key: 'First Name', label: 'First Name' },
                          { key: 'Last Name', label: 'Last Name' },
                          { key: 'Date Of Birth', label: 'Date Of Birth' },
                          { key: 'License Number', label: 'License Number' },
                        ]}
                        emptyLabel="No driver details found."
                      />
                    )}

                    {previewTab === 'Fees' && (() => {
                      const isReadOnly = !!previewTx.isUpdateInNowCerts;
                      return (
                        <div className="acord-smartscan-history__fees-table-wrap">
                          <table className="acord-smartscan-history__fees-table">
                            <thead>
                              <tr className="acord-smartscan-history__fees-row-border">
                                <th className="acord-smartscan-history__fees-th">Description</th>
                                <th className="acord-smartscan-history__fees-th">Amount</th>
                                <th className="acord-smartscan-history__fees-th">Mapping In AMS</th>
                              </tr>
                            </thead>
                            <tbody>
                              {previewData.taxesAndFees.map((f: any, i: number) => (
                                <tr key={i} className="acord-smartscan-history__fees-row-border">
                                  <td className="acord-smartscan-history__fees-td acord-smartscan-history__fees-td--muted">{f.descriptionField || '—'}</td>
                                  <td className="acord-smartscan-history__fees-td acord-smartscan-history__fees-td--muted">{f.descriptionAmount || '—'}</td>
                                  <td className="acord-smartscan-history__fees-td">
                                    {isReadOnly ? (
                                      <span className="acord-smartscan-history__fees-readonly-value">{f.mappingBillingType || findBillingTypeByValue(f.mappingBillingTypeValue)?.name || '—'}</span>
                                    ) : (
                                      <select
                                        value={f.mappingBillingTypeValue ?? ''}
                                        onChange={(e) => {
                                          const value = Number(e.target.value);
                                          const bt = findBillingTypeByValue(value);
                                          updatePreviewFee(i, { mappingBillingType: bt?.name ?? '', mappingBillingTypeValue: value });
                                        }}
                                        className="acord-smartscan-history__fee-select"
                                      >
                                        <option value="" style={optionStyle}>Select fee / tax type</option>
                                        {BILLING_TYPE_DATA.map((bt) => (
                                          <option key={bt.value} value={bt.value} style={optionStyle}>{bt.name}</option>
                                        ))}
                                      </select>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      );
                    })()}

                    {/* Submit — available on every tab whenever this record hasn't been synced yet */}
                    {previewSubmitResults.length > 0 && (
                      <div className="acord-smartscan-history__submit-results">
                        {previewSubmitResults.map((r, i) => (
                          <div key={i} className="acord-smartscan-history__result-row" style={{ color: r.success ? '#10b981' : '#ef4444' }}>
                            {r.success ? <FiCheckCircle size={13} /> : <FiAlertCircle size={13} />}
                            <strong>{r.label}:</strong> {r.message}
                          </div>
                        ))}
                      </div>
                    )}

                    {!previewTx.isUpdateInNowCerts && (
                      <div className="acord-smartscan-history__actions-row">
                        <motion.button className="cs-btn-start" whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} disabled={previewSubmitLoading} onClick={onPreviewSubmitClick}>
                          {previewSubmitLoading ? 'Submitting…' : 'Submit'}
                        </motion.button>
                      </div>
                    )}

                    {previewNcUrl && previewCreatedNewClient && (
                      <div className="acord-smartscan-history__actions-row-end">
                        <motion.button className="cs-btn-start" whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} onClick={() => window.open(previewNcUrl, '_blank')}>
                          {previewSelectedOptions?.SelectProspect ? 'Open Prospect' : 'Open Insured'}
                        </motion.button>
                      </div>
                    )}

                    {previewShowSubmitConfirm && ReactDOM.createPortal(
                      <div className="csams-overlay" onClick={() => setPreviewShowSubmitConfirm(false)}>
                        <div className="csams-modal" onClick={(e) => e.stopPropagation()}>
                          <div className="csams-modal__header">
                            <h3 className="csams-modal__title">Confirm Submit</h3>
                            <button className="csams-modal__close" onClick={() => setPreviewShowSubmitConfirm(false)}>✕</button>
                          </div>
                          <div className="csams-modal__body">
                            <p className="acord-smartscan-history__confirm-text">
                              By clicking <span className="acord-smartscan-history__confirm-highlight">Submit</span>, a new client/quote will be created in NowCerts. Are you sure you want to proceed?
                            </p>
                          </div>
                          <div className="csams-modal__footer">
                            <button className="csams-btn csams-btn--cancel" onClick={() => setPreviewShowSubmitConfirm(false)}>Cancel</button>
                            <button className="csams-btn csams-btn--upload" onClick={onPreviewSubmitConfirmed}>Confirm</button>
                          </div>
                        </div>
                      </div>,
                      document.body,
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
};

export default AcordSmartScanHistory;
